<template>
    <div class="lane-target-cards">
        <div class="cards">
            <div v-for="t in cards" :key="t.tier" class="target-card" :class="{'is-selected': isSelected(t)}" @click="$emit('select', t)">
                <div class="card-top">
                    <span class="lane-chip">{{ t.tier }}</span>
                    <span class="gain">+{{ t.diff | money }}<span class="per"> / yr</span></span>
                </div>
                <div class="hours-line"><strong>{{ t.requiredHours }} clock hours</strong> to get there</div>
                <div class="bundle-line" v-if="t.bundle.fits">
                    About <strong>{{ t.bundle.cost | money }}</strong> as a bundle &mdash; your raise covers it in about <strong>{{ t.bundle.months }} {{ t.bundle.months === 1 ? 'month' : 'months' }}</strong>.
                </div>
                <div class="bundle-line" v-else>
                    Start with a <strong>{{ t.bundle.hours }}-hour bundle</strong> ({{ t.bundle.cost | money }}) and build another when it wraps up &mdash; the raise covers the full move in about <strong>{{ t.bundle.months }} months</strong>.
                </div>
                <div class="card-actions">
                    <span class="selected-note" v-if="isSelected(t)"><i class="fas fa-check mr-1"></i>Shown in the charts below</span>
                    <b-button type="is-primary" size="is-small" tag="router-link" :to="builderRoute(t)" @click.native.stop><i class="fas fa-layer-group mr-2"></i>Build This Bundle</b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {priceForHours, HOURS_MAX} from "../../constants/packages";

    /**
     * One card per salary column clock hours can reach from where the
     * student is: the hours it takes, what it adds per year, what the hours
     * cost as a bundle and how fast the raise pays that back. Clicking a card
     * makes it the lane the charts follow; the button opens the bundle
     * builder sized to the move (capped at the largest bundle we sell).
     */
    export default {
        name: "LaneTargetCards",
        props: {
            targets: {type: Array, default: () => []}, // computePosition().targets
            selected: {type: Object, default: null},
        },
        computed: {
            cards() {
                return this.targets.map(t => {
                    const fits = t.requiredHours <= HOURS_MAX;
                    const hours = fits ? t.requiredHours : HOURS_MAX;
                    const fullCost = priceForHours(t.requiredHours);
                    return {
                        ...t,
                        bundle: {
                            fits,
                            hours,
                            cost: priceForHours(hours),
                            months: t.diff > 0 ? Math.max(1, Math.ceil(fullCost / (t.diff / 12))) : null,
                        }
                    };
                });
            }
        },
        methods: {
            isSelected(t) {
                return !!(this.selected && this.selected.tier === t.tier);
            },
            // Straight to "Your Needs" with the hours preset; the step still
            // validates them
            builderRoute(t) {
                return {name: 'package-builder', query: {phase: 'wizard', step: '1', hours: String(t.bundle.hours)}};
            }
        }
    };
</script>

<style lang="scss" scoped>
    .cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 1rem;
    }

    .target-card {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        background: #fafbfc;
        border: 2px solid #e6eaef;
        border-radius: 8px;
        padding: 1rem 1.1rem;
        cursor: pointer;
        transition: border-color 150ms, box-shadow 150ms;

        &:hover {
            border-color: #b9cbe0;
        }

        &.is-selected {
            border-color: #336699;
            background: #f2f7fc;
        }

        .card-top {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 0.75rem;
            flex-wrap: wrap;
        }

        .lane-chip {
            display: inline-block;
            background: #2e5f8f;
            color: #fff;
            font-size: 0.75rem;
            font-weight: bold;
            padding: 3px 10px;
            border-radius: 999px;
            white-space: nowrap;
        }

        .gain {
            font-size: 1.15rem;
            font-weight: 900;
            color: #2e7d4f;

            .per {
                font-size: 0.8rem;
                font-weight: 600;
                color: #666d7a;
            }
        }

        .hours-line {
            font-size: 0.95rem;
            color: #33475b;
        }

        .bundle-line {
            font-size: 0.82rem;
            color: #666d7a;
        }

        .card-actions {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.5rem;
            margin-top: auto;
            padding-top: 0.5rem;
            flex-wrap: wrap;

            .selected-note {
                font-size: 0.75rem;
                font-weight: 700;
                color: #336699;
            }
        }
    }
</style>
